import { useMemo } from 'react';
import { EMPTY_ARRAY } from '~/core/util/array';
import { selectors as insurancePlansSelectors } from '~/ducks/insuranceCarrierPlans';
import { InsuranceCarrierPlan } from '~/ducks/insuranceCarrierPlans';
import { useSolvSelector } from '~/reducers';
import { InsuranceCarrier } from '~/reducers/insurers';
import { PaperworkFieldName } from '../../paperworkConstants';
import { getStandardFormFieldData } from '../../util';

export const otherMetaOption = { _meta: 'other' } as const;
export const unknownMetaOption = { _meta: 'unknown' } as const;
export type MetaOption = typeof otherMetaOption | typeof unknownMetaOption;
export type InsuranceCarrierOption = InsuranceCarrier | MetaOption;
export type InsurancePlanOption = InsuranceCarrierPlan | MetaOption;

export const isMetaOption = (
  option: InsuranceCarrierOption | InsurancePlanOption | null | undefined
): option is MetaOption => {
  return !!option && typeof option === 'object' && '_meta' in option;
};

export const isMetaUnknown = (
  option: InsuranceCarrierOption | InsurancePlanOption | null | undefined
): option is typeof unknownMetaOption => {
  return isMetaOption(option) && option._meta === 'unknown';
};

export const isMetaOther = (
  option: InsuranceCarrierOption | InsurancePlanOption | null | undefined
): option is typeof otherMetaOption => {
  return isMetaOption(option) && option._meta === 'other';
};

export const renderInsuranceCarrierOptionLabel = (option: InsuranceCarrierOption) => {
  if (!option) {
    return '';
  }
  if (isMetaOther(option)) {
    return 'Other';
  }
  if (isMetaUnknown(option)) {
    return 'Unknown';
  }
  return option.display_name;
};

export const renderInsurancePlanOptionLabel = (option: InsurancePlanOption) => {
  if (!option) {
    return '';
  }
  if (isMetaOther(option)) {
    return 'Other';
  }
  if (isMetaUnknown(option)) {
    return 'Unknown';
  }
  return option.insurance_plan_display_name;
};

export const useShouldPrimaryInsuranceCarrierAutocompleteBeAdministered = (paperwork: any) => {
  const primaryInsuranceCarrierField = getStandardFormFieldData(
    PaperworkFieldName.PRIMARY_INSURANCE_COMPANY,
    paperwork
  );

  const isInsurancePlanNameRequired = useSolvSelector(
    (state) => (state.location as any)?.isInsurancePlanNameRequired
  );

  return !!(primaryInsuranceCarrierField && isInsurancePlanNameRequired);
};

type ProcessFormValuesForAutocompleteAnswersResult = [
  formValues: any,
  autoCompleteOptions: {
    [PaperworkFieldName.PRIMARY_INSURANCE_COMPANY]?: InsuranceCarrierOption;
    [PaperworkFieldName.PRIMARY_INSURANCE_PLAN_NAME]?: InsurancePlanOption;
  }
];

/**
 * This purpose of this function is to process a antd form values payload, find any autocomplete form fields,
 * and replace their values with the string-label representation of those options. The selected options
 * are separately returned in an object.
 *
 * @param formValues The result of running antd form's validateFields() method
 * @returns a tuple containing (0) the form values with no autocomplete-option values, and (1) all identified autocomplete options
 */
export const processFormValuesForAutocompleteAnswers = (
  formValues: any
): ProcessFormValuesForAutocompleteAnswersResult => {
  if (!formValues || typeof formValues !== 'object') {
    return [formValues, {}];
  }
  const toReturn = { ...formValues };
  let carrierOption: InsuranceCarrierOption | null = null;
  let planOption: InsurancePlanOption | null = null;
  if (
    toReturn[PaperworkFieldName.PRIMARY_INSURANCE_COMPANY] != null &&
    typeof toReturn[PaperworkFieldName.PRIMARY_INSURANCE_COMPANY] === 'object'
  ) {
    carrierOption = toReturn[
      PaperworkFieldName.PRIMARY_INSURANCE_COMPANY
    ] as InsuranceCarrierOption;
    toReturn[PaperworkFieldName.PRIMARY_INSURANCE_COMPANY] =
      renderInsuranceCarrierOptionLabel(carrierOption);
  }
  if (
    toReturn[PaperworkFieldName.PRIMARY_INSURANCE_PLAN_NAME] != null &&
    typeof toReturn[PaperworkFieldName.PRIMARY_INSURANCE_PLAN_NAME] === 'object'
  ) {
    planOption = toReturn[PaperworkFieldName.PRIMARY_INSURANCE_PLAN_NAME] as InsurancePlanOption;
    toReturn[PaperworkFieldName.PRIMARY_INSURANCE_PLAN_NAME] =
      renderInsurancePlanOptionLabel(planOption);
  }
  return [
    toReturn,
    {
      ...(!!carrierOption && {
        [PaperworkFieldName.PRIMARY_INSURANCE_COMPANY]: carrierOption,
      }),
      ...(!!planOption && {
        [PaperworkFieldName.PRIMARY_INSURANCE_PLAN_NAME]: planOption,
      }),
    },
  ];
};

export const useInitialCarrierOption = (
  acceptedInsuranceCarriers: InsuranceCarrier[],
  initialCarrierOptionLabel: string | null | undefined
) =>
  useMemo(() => {
    const searchVal = initialCarrierOptionLabel?.trim().toLowerCase();
    if (!searchVal) return null;
    return (
      acceptedInsuranceCarriers.find(
        (insurer) => insurer.display_name?.trim()?.toLowerCase() === searchVal
      ) ?? null
    );
  }, [acceptedInsuranceCarriers, initialCarrierOptionLabel]);

export const useAcceptedInsurancePlans = (selectedInsuranceCarrier: InsuranceCarrier | null) =>
  useSolvSelector((state) => {
    if (selectedInsuranceCarrier == null) {
      return EMPTY_ARRAY;
    }
    return insurancePlansSelectors.getInsurancePlansForInsurer(
      state,
      selectedInsuranceCarrier?.insurer_code
    );
  });
